import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Users, UserCheck, Briefcase, IndianRupee, Calendar, 
  Search, Plus, AlertCircle,
  ChevronRight, Filter, Gift, UserPlus
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import ChangeHistoryButton from '../components/ChangeHistoryButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import EmployeeDetailSheet from '../components/EmployeeDetailSheet';
import CreateEmployeeDialog from '../components/CreateEmployeeDialog';
import SidebarLayout from '../components/SidebarLayout';

import API from '../utils/api';

const DEPARTMENTS = [
  "Founder's Office",
  "Social & Content",
  "Video & Motion",
  "Creative & Design",
  "Paid & Performance"
];

const EMPLOYMENT_TYPES = ["Intern", "Full-time"];
const STATUSES = ["Active", "Inactive"];

export default function EmployeeTrackerPage() {
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/employees`, { 
          withCredentials: true,
          params: {
            search: search || undefined,
            department: departmentFilter && departmentFilter !== 'all' ? departmentFilter : undefined,
            employment_type: typeFilter && typeFilter !== 'all' ? typeFilter : undefined,
            status: statusFilter && statusFilter !== 'all' ? statusFilter : undefined
          }
        }),
      ]);
      setStats(statsRes.data);
      setEmployees(employeesRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [search, departmentFilter, typeFilter, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <SidebarLayout title="Employees" subtitle="Employee Tracker">
        <div className="flex items-center justify-center py-20">
          <div className="text-white text-lg">Loading...</div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout title="Employees" subtitle="Employee Tracker">
      {/* KPI Cards */}
      <section className="mb-8 animate-fadeIn">
        <div className="kpi-scroll md:grid-cols-3 lg:grid-cols-5 gap-4">
          <KPICard icon={<Users className="text-indigo-400" />} label="Total Employees" value={stats?.total_employees || 0} testId="kpi-total-employees" />
          <KPICard icon={<Briefcase className="text-cyan-400" />} label="Total Interns" value={stats?.total_interns || 0} testId="kpi-total-interns" />
          <KPICard icon={<UserCheck className="text-emerald-400" />} label="Active Employees" value={stats?.active_employees || 0} testId="kpi-active-employees" />
          <KPICard icon={<IndianRupee className="text-amber-400" />} label="Monthly Payroll" value={formatCurrency(stats?.total_payroll || 0)} testId="kpi-total-payroll" />
          <KPICard icon={<Calendar className="text-rose-400" />} label="Upcoming Reviews" value={stats?.upcoming_reviews_count || 0} accent testId="kpi-upcoming-reviews" />
        </div>
      </section>

      {/* Alerts */}
      <section className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
        <AlertCard
          icon={<AlertCircle className="text-rose-400" />}
          title="Review Alerts"
          items={stats?.upcoming_reviews || []}
          renderItem={(item) => (
            <div key={item.employee_id} className="flex justify-between items-center">
              <span className="text-slate-300">{item.name}</span>
              <span className="text-xs text-slate-500">{formatDate(item.review_date)}</span>
            </div>
          )}
          emptyText="No upcoming reviews"
          accentColor="border-l-rose-500"
          testId="alert-reviews"
        />
        <AlertCard
          icon={<Gift className="text-emerald-400" />}
          title="Upcoming Birthdays"
          items={stats?.upcoming_birthdays || []}
          renderItem={(item) => (
            <div key={item.employee_id} className="flex justify-between items-center">
              <span className="text-slate-300">{item.name}</span>
              <span className="text-xs text-emerald-400">
                {item.days_until === 0 ? 'Today!' : `${item.days_until} days`}
              </span>
            </div>
          )}
          emptyText="No upcoming birthdays"
          accentColor="border-l-emerald-500"
          testId="alert-birthdays"
        />
        <AlertCard
          icon={<UserPlus className="text-cyan-400" />}
          title="New Joinees"
          items={stats?.new_joinees || []}
          renderItem={(item) => (
            <div key={item.employee_id} className="flex justify-between items-center">
              <span className="text-slate-300">{item.name}</span>
              <span className="text-xs text-slate-500">{item.department}</span>
            </div>
          )}
          emptyText="No new joinees this month"
          accentColor="border-l-cyan-500"
          testId="alert-joinees"
        />
      </section>

      {/* Search & Filter */}
      <section className="mb-6 glass-card p-4 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <Input
              type="text"
              placeholder="Search by name or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-black/40 border-white/10 text-white placeholder:text-slate-500 focus:border-cyan-400"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-3 flex-wrap">
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-full md:w-[180px] bg-black/40 border-white/10 text-slate-300" data-testid="filter-department">
                <Filter size={14} className="mr-2" />
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10">
                <SelectItem value="all">All Departments</SelectItem>
                {DEPARTMENTS.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full md:w-[140px] bg-black/40 border-white/10 text-slate-300" data-testid="filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10">
                <SelectItem value="all">All Types</SelectItem>
                {EMPLOYMENT_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[130px] bg-black/40 border-white/10 text-slate-300" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10">
                <SelectItem value="all">All Status</SelectItem>
                {STATUSES.map(status => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={() => setShowCreateDialog(true)}
              className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow-[0_0_15px_rgba(79,70,229,0.3)]"
              data-testid="create-employee-button"
            >
              <Plus size={18} className="mr-2" />
              Create Employee
            </Button>
          </div>
        </div>
      </section>

      {/* Employee Table */}
      <section className="glass-card overflow-hidden animate-fadeIn" style={{ animationDelay: '0.3s' }}>
        <div className="overflow-x-auto mobile-cards">
          <table className="data-table" data-testid="employee-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Department</th>
                <th>Type</th>
                <th>Joining Date</th>
                <th>Total Salary</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-500">
                    No employees found
                  </td>
                </tr>
              ) : (
                employees.map((emp) => (
                  <tr key={emp.id} onClick={() => setSelectedEmployee(emp)} data-testid={`employee-row-${emp.id}`}>
                    <td>
                      <div>
                        <div className="text-white font-medium">{emp.name}</div>
                        <div className="text-xs font-mono text-cyan-400">{emp.employee_id}</div>
                      </div>
                    </td>
                    <td>{emp.role}</td>
                    <td>{emp.department}</td>
                    <td>
                      <span className={`text-xs px-2 py-1 rounded ${emp.employment_type === 'Intern' ? 'bg-cyan-500/10 text-cyan-400' : 'bg-indigo-500/10 text-indigo-400'}`}>
                        {emp.employment_type}
                      </span>
                    </td>
                    <td>{formatDate(emp.joining_date)}</td>
                    <td className="font-mono">{formatCurrency(emp.total_salary)}</td>
                    <td>
                      <span className={`status-badge ${emp.status.toLowerCase()}`}>
                        {emp.status}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {isAdmin && <ChangeHistoryButton resource="employees" recordId={emp.employee_id} />}
                        <ChevronRight size={16} className="text-slate-500" />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {selectedEmployee && (
        <EmployeeDetailSheet
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
          onUpdate={fetchData}
        />
      )}

      {showCreateDialog && (
        <CreateEmployeeDialog
          onClose={() => setShowCreateDialog(false)}
          onCreated={fetchData}
        />
      )}
    </SidebarLayout>
  );
}

function KPICard({ icon, label, value, accent, testId }) {
  return (
    <div className={`kpi-card ${accent ? 'border-rose-500/30' : ''}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-4">
        <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">{label}</span>
        {icon}
      </div>
      <div className="font-heading text-xl md:text-2xl font-black tracking-tight text-white truncate">{value}</div>
    </div>
  );
}

function AlertCard({ icon, title, items, renderItem, emptyText, accentColor, testId }) {
  return (
    <div className={`alert-card border-l-4 ${accentColor}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h3 className="font-semibold text-white">{title}</h3>
        {items.length > 0 && (
          <span className="ml-auto text-xs bg-white/10 px-2 py-0.5 rounded-full text-slate-300">{items.length}</span>
        )}
      </div>
      <div className="space-y-3">
        {items.length === 0 ? (
          <p className="text-sm text-slate-500">{emptyText}</p>
        ) : (
          items.slice(0, 5).map(renderItem)
        )}
      </div>
    </div>
  );
}
